import React, { useState, useEffect } from "react";
import { useApi, apiPost, apiPut } from "../hooks/useApi.ts";
import { ProjectList } from "./ProjectList.tsx";

interface ConfigData {
  active: {
    teamwork: { baseUrl: string; hasApiKey: boolean };
    fellow: { subdomain: string; hasApiKey: boolean };
    github: { hasToken: boolean };
    resend: { fromEmail: string; hasApiKey: boolean };
    ai: { provider: string; model: string; hasApiKey: boolean };
    reminder: { daysBefore: number[]; pollIntervalMinutes: number };
  };
}

interface ConnectionResults {
  teamwork: boolean;
  fellow: boolean;
  github: boolean;
  resend: boolean;
}

export function ConfigPanel() {
  const { data: config, loading, refetch } = useApi<ConfigData>("/api/config");
  const [testing, setTesting] = useState(false);
  const [connections, setConnections] = useState<ConnectionResults | null>(null);
  const [daysBefore, setDaysBefore] = useState("");
  const [pollInterval, setPollInterval] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [teamworkKey, setTeamworkKey] = useState("");
  const [fellowKey, setFellowKey] = useState("");
  const [githubToken, setGithubToken] = useState("");
  const [savingKeys, setSavingKeys] = useState(false);
  const [keysSaved, setKeysSaved] = useState(false);

  useEffect(() => {
    if (config) {
      setDaysBefore(config.active.reminder.daysBefore.join(", "));
      setPollInterval(String(config.active.reminder.pollIntervalMinutes));
    }
  }, [config]);

  const saveReminders = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await apiPut("/api/config", {
        reminder_days_before: daysBefore.split(",").map((s) => s.trim()).join(","),
        reminder_poll_interval: pollInterval,
      });
      setSaved(true);
      refetch();
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const testConnections = async () => {
    setTesting(true);
    try {
      const results = await apiPost<ConnectionResults>("/api/config/test-connections", {});
      setConnections(results);
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <div className="loading">Loading config...</div>;
  if (!config) return null;

  const { active } = config;

  return (
    <div>
      <div className="page-header">
        <h1>Settings</h1>
        <p>API connections and configuration</p>
      </div>

      <div className="card">
        <div className="card-header">
          <h2>API Connections</h2>
          <button className="btn btn-primary btn-sm" onClick={testConnections} disabled={testing}>
            {testing ? "Testing..." : "Test All Connections"}
          </button>
        </div>

        <table>
          <thead>
            <tr>
              <th>Service</th>
              <th>Config</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Teamwork</strong></td>
              <td>
                {active.teamwork.baseUrl || "Not configured"}
                {active.teamwork.hasApiKey ? " (key set)" : " (no key)"}
              </td>
              <td>
                {connections && (
                  <span className={`status-dot ${connections.teamwork ? "connected" : "disconnected"}`} />
                )}
                {connections && (connections.teamwork ? " Connected" : " Failed")}
              </td>
            </tr>
            <tr>
              <td><strong>Fellow</strong></td>
              <td>
                {active.fellow.subdomain || "Not configured"}
                {active.fellow.hasApiKey ? " (key set)" : " (no key)"}
              </td>
              <td>
                {connections && (
                  <span className={`status-dot ${connections.fellow ? "connected" : "disconnected"}`} />
                )}
                {connections && (connections.fellow ? " Connected" : " Failed")}
              </td>
            </tr>
            <tr>
              <td><strong>GitHub</strong></td>
              <td>
                {active.github.hasToken ? "Token set" : "No token"}
              </td>
              <td>
                {connections && (
                  <span className={`status-dot ${connections.github ? "connected" : "disconnected"}`} />
                )}
                {connections && (connections.github ? " Connected" : " Failed")}
              </td>
            </tr>
            <tr>
              <td><strong>Resend</strong></td>
              <td>
                {active.resend.fromEmail}
                {active.resend.hasApiKey ? " (key set)" : " (no key)"}
              </td>
              <td>
                {connections && (
                  <span className={`status-dot ${connections.resend ? "connected" : "disconnected"}`} />
                )}
                {connections && (connections.resend ? " Connected" : " Failed")}
              </td>
            </tr>
            <tr>
              <td><strong>AI Provider</strong></td>
              <td>
                {active.ai.provider} / {active.ai.model}
                {active.ai.hasApiKey ? " (key set)" : " (no key)"}
              </td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="card">
        <div className="card-header">
          <h2>Reminder Settings</h2>
        </div>
        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
          <div className="form-group" style={{ flex: 1, minWidth: "200px" }}>
            <label>Reminder Days Before Due</label>
            <input
              type="text"
              value={daysBefore}
              onChange={(e) => { setDaysBefore(e.target.value); setSaved(false); }}
              placeholder="e.g. 7, 3, 1"
            />
            <span style={{ fontSize: "12px", color: "var(--color-text-muted)", marginTop: "4px", display: "block" }}>
              Comma-separated list of days
            </span>
          </div>
          <div className="form-group" style={{ flex: 1, minWidth: "200px" }}>
            <label>Fellow Poll Interval (minutes)</label>
            <input
              type="number"
              value={pollInterval}
              onChange={(e) => { setPollInterval(e.target.value); setSaved(false); }}
              min={1}
            />
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "12px", marginTop: "8px" }}>
          {saved && <span style={{ color: "var(--color-success, #22c55e)", fontSize: "13px" }}>Saved!</span>}
          <button className="btn btn-primary btn-sm" onClick={saveReminders} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2>API Keys</h2>
        </div>
        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
          <div className="form-group" style={{ flex: 1, minWidth: "200px" }}>
            <label>Teamwork API Key</label>
            <input
              type="password"
              value={teamworkKey}
              onChange={(e) => { setTeamworkKey(e.target.value); setKeysSaved(false); }}
              placeholder={active.teamwork.hasApiKey ? "Key configured" : "Enter API key"}
            />
          </div>
          <div className="form-group" style={{ flex: 1, minWidth: "200px" }}>
            <label>Fellow API Key</label>
            <input
              type="password"
              value={fellowKey}
              onChange={(e) => { setFellowKey(e.target.value); setKeysSaved(false); }}
              placeholder={active.fellow.hasApiKey ? "Key configured" : "Enter API key"}
            />
          </div>
          <div className="form-group" style={{ flex: 1, minWidth: "200px" }}>
            <label>GitHub Token</label>
            <input
              type="password"
              value={githubToken}
              onChange={(e) => { setGithubToken(e.target.value); setKeysSaved(false); }}
              placeholder={active.github.hasToken ? "Token configured" : "Enter personal access token"}
            />
            <span style={{ fontSize: "12px", color: "var(--color-text-muted)", marginTop: "4px", display: "block" }}>
              Used for codebase-aware task estimates and QA automation. For QA (committing tests/workflows
              and opening issues), the token needs <code>repo</code> + <code>workflow</code> scope (classic)
              or Contents, Issues &amp; Workflows write (fine-grained).
            </span>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "12px", marginTop: "8px" }}>
          {keysSaved && <span style={{ color: "var(--color-success, #22c55e)", fontSize: "13px" }}>Saved!</span>}
          <button
            className="btn btn-primary btn-sm"
            disabled={savingKeys || (!teamworkKey && !fellowKey && !githubToken)}
            onClick={async () => {
              setSavingKeys(true);
              setKeysSaved(false);
              try {
                const body: Record<string, string> = {};
                if (teamworkKey) body.teamwork_api_key = teamworkKey;
                if (fellowKey) body.fellow_api_key = fellowKey;
                if (githubToken) body.github_token = githubToken;
                await apiPut("/api/config", body);
                setTeamworkKey("");
                setFellowKey("");
                setGithubToken("");
                setKeysSaved(true);
                refetch();
                // Auto-test connections after saving
                const results = await apiPost<ConnectionResults>("/api/config/test-connections", {});
                setConnections(results);
              } catch (err) {
                alert((err as Error).message);
              } finally {
                setSavingKeys(false);
              }
            }}
          >
            {savingKeys ? "Saving..." : "Save Keys"}
          </button>
        </div>
      </div>

      <ProjectList />
    </div>
  );
}
